import { z } from 'zod';
import { BenefitCategory } from 'store/benefits';
import { UploadedFile } from 'store/files';
import { Translations } from 'types/Common';
import { Currency, TimeUnit } from 'types/general';
import { ShopItemCategory } from 'types/MarketplaceItems';
import {
  shopItemsByBenefitFiltersSchema,
  shopItemsFiltersSchema,
} from './shop.schemas';

export const shopItemStatuses = ['active', 'inactive', 'archived'] as const;
export type ShopItemStatus = (typeof shopItemStatuses)[number];

export const shopItemTypes = ['GIFTCARD', 'SERVICE', 'SUBSCRIPTION'] as const;
export type ShopItemType = (typeof shopItemTypes)[number];

export const SHOP_ITEM_TYPE_LABELS: Record<ShopItemType, string> = {
  GIFTCARD: 'Giftcard',
  SERVICE: 'Service',
  SUBSCRIPTION: 'Subscription',
};

export const priceVariants = ['fixedPrice', 'priceRange'] as const;
export type PriceVariant = (typeof priceVariants)[number];

export const shopItemsByBenefitListVariants = [
  'all',
  'recommended',
  'selected',
] as const;
export type ShopItemsByBenefitListVariant =
  (typeof shopItemsByBenefitListVariants)[number];

export const taxExemptionStatuses = [
  'exempt',
  'non-exempt',
  'undefined',
] as const;
export type TaxExemptionStatus = (typeof taxExemptionStatuses)[number];

export interface BaseShopItem {
  id: string;
  name: string;
  type: ShopItemType;
  image: UploadedFile | null;
  sku: string;
  vatRate: number;
  currency: Currency;
  countries: string[];
  countriesAlpha2: string[];
  cities: string[];
  benefitPlanTypes: BenefitCategory[];
  categories: ShopItemCategory[];
  status: ShopItemStatus;
  supplier: {
    id: string;
    name: string;
    logo: UploadedFile | null;
    termsAndConditionsLink?: string;
    additionalInfo: string;
    isParentCompany: boolean;
  };
  priority: number;
  searchTags: string;
  resolutionDuration: number;
  resolutionDurationUnit: TimeUnit;
  translations: Translations<{
    id: string;
    title: string;
    description: string;
  }>;
  inSelection: boolean;
  createdAt: string;
  taxExemptions: Array<{
    id: string;
    country: string;
    status: TaxExemptionStatus;
  }>;
}

export interface ShopItem extends BaseShopItem {
  prices: Array<{
    id: string;
    price: number | null;
    priceRangeFrom: number | null;
    priceRangeTo: number | null;
    priceType: PriceVariant;
    discountPercentage: number;
    translations: Translations<{ description: string }>;
    index: number;
    inventory: number;
  }>;
}

export interface ShopItemByBenefit extends ShopItem {
  assignment: {
    autoAssigned: boolean;
  } | null;
}

export type ShopItemsFilters = z.infer<typeof shopItemsFiltersSchema>;
export type ShopItemsByBenefitFilters = z.infer<
  typeof shopItemsByBenefitFiltersSchema
>;
